import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';
import './index.css';

import Expenses from './pages/Expenses';
import PersonalExpenses from './pages/PersonalExpenses';
import MonthlyCategoryBreakdown from './pages/MonthlyCategoryBreakdown';
import Tenants from './pages/Tenants';
import Invoices from './pages/Invoices';
import InwardMoney from './pages/InwardMoney';
import Inventory from './pages/Inventory';
import InvestmentAnalytics from './pages/InvestmentAnalytics';
import InitialSetupExpenses from './pages/InitialSetupExpenses';
import { authService, isDemoMode } from './services/supabaseService';

function App() {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check initial active session
    const initAuth = async () => {
      try {
        const activeSession = await authService.getSession();
        if (activeSession) {
          setSession(activeSession);
          setUser(activeSession.user || null);
        }
      } catch (err) {
        console.error('Error fetching auth session:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Subscribe to auth changes (Sign in, Sign out, Token Refresh)
    const { data: authSubscription } = authService.onAuthStateChange((event, newSession) => {
      if (!isDemoMode()) {
        setSession(newSession);
        setUser(newSession?.user || null);
      }
    });

    return () => {
      if (authSubscription?.subscription?.unsubscribe) {
        authSubscription.subscription.unsubscribe();
      }
    };
  }, []);

  const handleLogin = (authenticatedUser, authenticatedSession) => {
    setUser(authenticatedUser);
    setSession(authenticatedSession);
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setSession(null);
    }
  };

  const isDemo = Boolean(user?.isDemo || isDemoMode());
  const isAuthenticated = Boolean((session && user) || isDemo);

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-main, #0f172a)',
        color: 'var(--text-main, #f8fafc)',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: 'var(--primary-color, #6366f1)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #94a3b8)' }}>Initializing session...</p>
      </div>
    );
  }

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <Routes>
        <Route 
          path="/login" 
          element={!isAuthenticated ? <AdminLogin onLogin={handleLogin} /> : <Navigate to="/" />} 
        />
        
        {/* Protected Routes */}
        <Route 
          path="/" 
          element={isAuthenticated ? <Layout user={{ ...user, isDemo }} onLogout={handleLogout} /> : <Navigate to="/login" />}
        >
          <Route index element={<Dashboard isDemo={isDemo} />} />
          
          {/* All routes accessible in both Production and Demo / Sandbox mode */}
          <Route path="expenses" element={<Expenses />} />
          <Route path="personal-expenses" element={<PersonalExpenses />} />
          <Route path="category-breakdown" element={<MonthlyCategoryBreakdown />} />
          <Route path="initial-setup-expenses" element={<InitialSetupExpenses />} />
          <Route path="investment-analytics" element={<InvestmentAnalytics />} />
          <Route path="tenants" element={<Tenants />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="inward-money" element={<InwardMoney />} />
          <Route path="inventory" element={<Inventory />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
